import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiSearch, FiChevronDown, FiX } from 'react-icons/fi';
import Icon from '../Icon';

type Option = {
    id: string;
    label: string;
    value: string;
    details?: {
        email?: string;
        mobile?: string;
    };
    address?: string;
};

type SearchableSelectProps = {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    className?: string;
};

export default function SearchableSelect({
    options,
    value,
    onChange,
    placeholder = 'Search and select...',
    label,
    disabled = false,
    className = '',
}: SearchableSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [displayValue, setDisplayValue] = useState('');
    const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        if (selectedOption) {
            setDisplayValue(`${selectedOption.label}${selectedOption.details?.email ? ` (${selectedOption.details.email})` : ''}`);
        } else {
            setDisplayValue('');
        }
    }, [selectedOption]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Also check if the click target is within the portal-rendered dropdown
                const portalDropdown = document.getElementById('searchable-select-portal-dropdown');
                if (portalDropdown && portalDropdown.contains(event.target as Node)) {
                    return;
                }
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Position tracking effect for Portal
    useEffect(() => {
        if (!isOpen || !containerRef.current) return;
        
        const updatePosition = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            setPanelPos({
                top: rect.bottom,
                left: rect.left,
                width: rect.width,
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    const filteredOptions = options.filter(opt => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            opt.label.toLowerCase().includes(search) ||
            opt.details?.email?.toLowerCase().includes(search) ||
            opt.details?.mobile?.includes(search) ||
            opt.address?.toLowerCase().includes(search)
        );
    });

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange('');
        setDisplayValue('');
        setSearchTerm('');
    };

    const portalTarget = typeof document !== 'undefined' ? document.body : null;

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {label && (
                <label className="form-field-label">{label}</label>
            )}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    className={`flex h-9 w-full items-center rounded-xl border border-gray-300 bg-gray-100 py-0 pl-10 pr-10 text-left text-sm text-gray-900 outline-none transition shadow-sm hover:bg-gray-50 focus:border-gray-500 focus:ring-2 focus:ring-gray-400/30 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 dark:hover:bg-black/40'
                        }`}
                >
                    <Icon
                        icon={FiSearch}
                        className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                    />
                    <span
                        className={`block truncate ${!displayValue ? 'font-light text-gray-400 dark:text-gray-400' : ''}`}
                    >
                        {displayValue || placeholder}
                    </span>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        {value && !disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiX} className="h-4 w-4 text-gray-400" />
                            </button>
                        )}
                        <Icon
                            icon={FiChevronDown}
                            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
            </div>

            {isOpen && panelPos && portalTarget && createPortal(
                <div 
                    id="searchable-select-portal-dropdown"
                    className="fixed z-[200] mt-1 rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
                    style={{
                        top: `${panelPos.top}px`,
                        left: `${panelPos.left}px`,
                        width: `${panelPos.width}px`,
                    }}
                >
                    <div className="border-b border-gray-200 p-2 dark:border-gray-700">
                        <div className="relative">
                            <Icon
                                icon={FiSearch}
                                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, email, or mobile..."
                                className="w-full rounded-lg border border-gray-300 bg-gray-100 py-2 pl-10 pr-4 text-sm font-normal text-gray-900 outline-none placeholder:font-light placeholder:text-gray-400 focus:border-gray-500 focus:ring-2 focus:ring-gray-400/30 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100 dark:placeholder:text-gray-400 dark:focus:ring-gray-500/25"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {filteredOptions.length === 0 ? (
                            <div className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                No managers found
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    className={`w-full px-4 py-3 text-left text-sm transition hover:bg-blue-50 dark:hover:bg-gray-700 ${value === option.value ? 'bg-blue-50 dark:bg-gray-700' : ''
                                        }`}
                                >
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {option.label}
                                    </div>
                                    {option.address && (
                                        <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5 leading-normal">
                                            {option.address}
                                        </div>
                                    )}
                                    {option.details?.email && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {option.details.email}
                                        </div>
                                    )}
                                    {option.details?.mobile && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {option.details.mobile}
                                        </div>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>,
                portalTarget
            )}

            {selectedOption && selectedOption.details && (
                <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs dark:border-blue-800 dark:bg-blue-900/20">
                    <div className="font-semibold text-blue-900 dark:text-blue-100">
                        {selectedOption.label}
                    </div>
                    {selectedOption.details.email && (
                        <div className="text-blue-700 dark:text-blue-300">
                            {selectedOption.details.email}
                        </div>
                    )}
                    {selectedOption.details.mobile && (
                        <div className="text-blue-700 dark:text-blue-300">
                            {selectedOption.details.mobile}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
